import createFetchClient, { type Middleware } from 'openapi-fetch'
import createQueryClient from 'openapi-react-query'

import * as authApi from '#lib/authApi'
import { API_URL } from '#lib/config'
import i18n from '#lib/i18n'
import { clearToken, getToken, setToken } from '#lib/tokenStore'

import { refreshAfterWriteMiddleware } from './refreshAfterWrite'
import type { paths } from './schema'

/**
 * Mutex: only one refresh in-flight at a time.
 * The access token is returned and not stored here, so a caller that lost the
 * race (a cancelled proactive refresh, or a profile load that a newer
 * sign-in replaced) can ignore it. Callers that still own the session store it.
 */
let refreshPromise: Promise<string> | null = null

export async function refreshTokenWithLock(): Promise<string> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async (): Promise<string> => {
    try {
      const response = await authApi.refresh()
      return response.token
    } finally {
      setTimeout(() => {
        refreshPromise = null
      }, 1000)
    }
  })()

  return refreshPromise
}

/** Injected via fetchClient.use() — openapi-fetch calls these hooks internally per request */
const authMiddleware: Middleware = {
  /** Injects Bearer token into every outgoing request */
  async onRequest({ request }): Promise<Request | undefined> {
    const token = getToken()
    if (token) request.headers.set('Authorization', `Bearer ${token}`)
    // The portal's language, not the browser's, so API messages match the screen (WM-001).
    request.headers.set('Accept-Language', i18n.language)

    return request
  },

  /** Intercepts 401 responses to refresh the token and retry the request */
  async onResponse({ request, response }): Promise<Response | undefined> {
    if (response.status !== 401) return response

    try {
      const newToken = await refreshTokenWithLock()
      setToken(newToken)
      const retryRequest = new Request(request, {
        headers: new Headers(request.headers),
      })
      retryRequest.headers.set('Authorization', `Bearer ${newToken}`)
      // Raw fetch intentionally bypasses middleware to avoid infinite 401 retry loop
      return fetch(retryRequest, { credentials: 'include' })
    } catch (error: unknown) {
      console.error('[auth] Token refresh failed, redirecting to login', error)
      clearToken()
      window.location.href = '/login'
      // Return a synthetic 401 to prevent TanStack Query error flash before navigation
      return new Response(null, { status: 401, statusText: 'Session expired' })
    }
  },
}

/** Client for Hydra/JSON-LD endpoints (collections, CRUD resources) */
export const fetchClient = createFetchClient<paths, 'application/ld+json'>({
  baseUrl: API_URL,
  credentials: 'include',
  headers: {
    Accept: 'application/ld+json',
  },
})

// Registered before auth: openapi-fetch runs onResponse in reverse, so this sees the response after a 401 retry.
fetchClient.use(refreshAfterWriteMiddleware, authMiddleware)

export const $api = createQueryClient(fetchClient)

/** Client for plain JSON endpoints (stats, auth, custom actions) */
export const fetchJsonClient = createFetchClient<paths, 'application/json'>({
  baseUrl: API_URL,
  credentials: 'include',
  headers: {
    Accept: 'application/json',
  },
})

fetchJsonClient.use(refreshAfterWriteMiddleware, authMiddleware)

export const $jsonApi = createQueryClient(fetchJsonClient)
